import type { CommentMention, ProjectMember } from '@outpost/api-client';

/**
 * Mentions in a comment, for writing one and for reading one back.
 *
 * A mention is `@` followed by a person — `@Ada Lovelace`, or their address —
 * and only where the `@` starts a token, the rule `outpost_core::mentions`
 * applies on the server, so that a name or an address quoted in prose is not a
 * mention here either.
 *
 * What this file deliberately does *not* do is decide who was named. The server
 * answers that, and reports the spelling each comment used; {@link
 * splitMentions} marks up exactly those spellings. Matching names locally
 * instead would highlight an `@` that reached nobody — or, worse, someone other
 * than the person the server told.
 */

/** Longest run of words the `@` menu follows before deciding this is prose. */
const MAX_WORDS = 4;

/**
 * Whether the character before an `@` makes it part of a word rather than the
 * start of a mention.
 *
 * The server's rule character for character (`opens_a_word`), including that
 * anything non-ASCII counts: `naïve@corp.com` is prose in any script.
 */
function opensAWord(character: string): boolean {
  return /[A-Za-z0-9._%+]/.test(character) || character.charCodeAt(0) > 127;
}

/**
 * Whether a character could have been part of the name or address that just
 * matched, which means the text spells something else. Mirrors
 * `extends_a_handle`.
 */
function extendsAHandle(character: string): boolean {
  return /[\p{L}\p{N}@._%+-]/u.test(character);
}

export type CommentSegment =
  | { kind: 'text'; value: string }
  /** `value` is what the comment wrote; `mention` is who that reached. */
  | { kind: 'mention'; value: string; mention: CommentMention };

/**
 * Split a comment body into plain text and the mentions that landed.
 *
 * `mentions` comes from the server and carries the spelling each one used, so
 * this only has to find those spellings: longest first, because one person's
 * name can begin another's.
 */
export function splitMentions(body: string, mentions: CommentMention[]): CommentSegment[] {
  if (mentions.length === 0) return body ? [{ kind: 'text', value: body }] : [];

  const spellings = [...mentions].sort((a, b) => b.named_as.length - a.named_as.length);
  const segments: CommentSegment[] = [];
  let plain = 0;

  for (let index = 0; index < body.length; index++) {
    if (body[index] !== '@') continue;
    if (index > 0 && opensAWord(body.charAt(index - 1))) continue;

    const rest = body.slice(index + 1);
    const mention = spellings.find((candidate) => spells(rest, candidate.named_as));
    if (!mention) continue;

    if (index > plain) segments.push({ kind: 'text', value: body.slice(plain, index) });
    const value = `@${rest.slice(0, mention.named_as.length)}`;
    segments.push({ kind: 'mention', value, mention });
    plain = index + value.length;
    index = plain - 1;
  }

  if (plain < body.length) segments.push({ kind: 'text', value: body.slice(plain) });
  return segments;
}

/** Whether `rest` begins with `named_as`, and the match ends cleanly. */
function spells(rest: string, named_as: string): boolean {
  if (rest.slice(0, named_as.length).toLowerCase() !== named_as.toLowerCase()) return false;
  const next = rest.charAt(named_as.length);
  return next === '' || !extendsAHandle(next);
}

/**
 * How to write a mention of `member` so the server resolves it back to them:
 * their name, or their address when the name would name two people at once.
 */
export function spell(member: ProjectMember, everyone: ProjectMember[]): string {
  const name = member.name.trim();
  if (name === '') return member.email;

  const shared = everyone.filter(
    (other) => other.name.trim().toLowerCase() === name.toLowerCase(),
  ).length;
  return shared > 1 ? member.email : name;
}

/** The `@…` being typed at the caret, and where it starts. */
export interface ActiveMention {
  /** What has been typed after the `@`, lowercased, for matching people. */
  query: string;
  start: number;
}

/**
 * The mention the caret is inside, or null.
 *
 * A name has spaces in it, so the query runs from the `@` to the caret rather
 * than to the end of a word — but only across a few words, or the menu would
 * follow a whole sentence. The `@` has to open a token: an address contains one
 * of its own, and `@ada@corp.` is one mention being written, not a second.
 */
export function activeMention(value: string, caret: number): ActiveMention | null {
  const line = value.lastIndexOf('\n', caret - 1) + 1;

  for (let index = caret - 1; index >= line; index--) {
    if (value[index] !== '@') continue;
    if (index > 0 && opensAWord(value.charAt(index - 1))) continue;

    const query = value.slice(index + 1, caret);
    if (query.split(' ').length > MAX_WORDS) return null;
    return { query: query.toLowerCase(), start: index };
  }
  return null;
}

/**
 * Replace the mention being typed with a whole name or address, ready to keep
 * writing.
 *
 * A space follows it, unless the text already continues with one: completing
 * mid-sentence should not leave a gap where the caret was.
 */
export function completeMention(
  value: string,
  active: ActiveMention,
  caret: number,
  spelling: string,
): { value: string; caret: number } {
  const spaced = /\s/.test(value.charAt(caret));
  const completed = `@${spelling}${spaced ? '' : ' '}`;
  return {
    value: value.slice(0, active.start) + completed + value.slice(caret),
    // Past the space either way, so the next character typed starts a word.
    caret: active.start + completed.length + (spaced ? 1 : 0),
  };
}
